import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router';

/** What a page can change in the shell at runtime (on top of its route handle). */
export interface ShellOverrides {
  /** Mobile top-bar title (e.g. the vehicle name). */
  title?: string;
  /** Mobile top-bar actions on the right (e.g. the favourite or filters button). */
  actions?: ReactNode;
  /** Hide the bottom bar (e.g. a chat thread is open). */
  hideBottomNav?: boolean;
}

interface Entry {
  path: string;
  overrides: ShellOverrides;
}

interface ShellApi {
  entry: Entry;
  set: (e: Entry) => void;
}

const Ctx = createContext<ShellApi | null>(null);

export function ShellProvider({ children }: { children: ReactNode }) {
  const [entry, set] = useState<Entry>({ path: '', overrides: {} });
  const value = useMemo(() => ({ entry, set }), [entry]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

const NONE: ShellOverrides = {};

/** Overrides of the current page only: a new page starts from its route handle. */
// eslint-disable-next-line react-refresh/only-export-components
export function useShellOverrides(): ShellOverrides {
  const ctx = useContext(Ctx);
  const { pathname } = useLocation();
  return ctx && ctx.entry.path === pathname ? ctx.entry.overrides : NONE;
}

/**
 * Lets a page adjust the shell while it is mounted:
 * `useShell({ title: vehicle.name, actions: <FavButton/> })`.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useShell(o: ShellOverrides) {
  const set = useContext(Ctx)?.set;
  const { pathname } = useLocation();
  const { title, actions, hideBottomNav } = o;
  useEffect(() => {
    set?.({ path: pathname, overrides: { title, actions, hideBottomNav } });
  }, [set, pathname, title, actions, hideBottomNav]);
}
